/* eslint-disable */
// src/layouts/security/AuthorizedDevicesPanel.js

import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import AppActionDialog from "components/common/AppActionDialog";
import AppSnackbar from "components/common/AppSnackbar";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import AuthorizedDeviceService from "services/authorizedDeviceService";
import SecurityColumnHeaders from "layouts/security/SecurityColumnHeaders";
import SecuritySectionTitle from "layouts/security/SecuritySectionTitle";
import { dangerOutlinedButtonSx, successContainedButtonSx } from "components/common/buttonStyles";
import { formatSecurityDateTime, getDeviceSummary } from "utils/securityDisplay";

const authorizedColumns = "minmax(0, 1.2fr) minmax(0, 1.2fr) minmax(0, 0.8fr) 9.5rem";
const pendingColumnTitles = ["Cuenta", "Dispositivo", "Solicitado"];
const authorizedColumnTitles = ["Cuenta", "Dispositivo", "Último uso"];

function DeviceRow({ device, onApprove, onRevoke, busy }) {
  const pending = device.Status === "pending";
  const active = device.Status === "active";
  const platform = getDeviceSummary(device.UserAgent);

  return (
    <MDBox
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "minmax(0, 1fr) minmax(0, 1fr)",
          lg: authorizedColumns,
        },
        alignItems: "center",
        gap: { xs: 1.5, sm: 2 },
        p: { xs: 2, sm: 2.5 },
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        backgroundColor: "background.paper",
        textAlign: "left",
      }}
    >
      <MDBox sx={{ minWidth: 0 }}>
        <MDTypography variant="body2" fontWeight="bold" sx={{ overflowWrap: "anywhere" }}>
          {device.Username || device.Email || "Usuario sin datos"}
        </MDTypography>
        {device.Username && device.Email && (
          <MDTypography
            variant="body2"
            color="text"
            display="block"
            sx={{ overflowWrap: "anywhere" }}
          >
            {device.Email}
          </MDTypography>
        )}
      </MDBox>

      <MDBox sx={{ minWidth: 0 }}>
        <MDTypography variant="body2" fontWeight="medium" sx={{ overflowWrap: "anywhere" }}>
          {device.Label || platform}
        </MDTypography>
        {device.Label && (
          <MDTypography variant="body2" color="text" display="block">
            {platform}
          </MDTypography>
        )}
      </MDBox>

      <MDBox sx={{ minWidth: 0 }}>
        <MDTypography variant="body2">
          {pending
            ? formatSecurityDateTime(device.RequestedAt)
            : active
              ? device.LastSeenAt
                ? formatSecurityDateTime(device.LastSeenAt)
                : "Sin uso registrado"
              : formatSecurityDateTime(device.RevokedAt)}
        </MDTypography>
      </MDBox>

      {(pending || active) && (
        <MDBox
          display="flex"
          justifyContent="flex-start"
          flexDirection={{ xs: "column", sm: "row", lg: pending ? "column" : "row" }}
          gap={1}
        >
          {pending && (
            <Button
              variant="contained"
              color="success"
              onClick={() => onApprove(device)}
              disabled={busy}
              sx={{ ...successContainedButtonSx, whiteSpace: "nowrap" }}
            >
              Aprobar
            </Button>
          )}
          <Button
            variant="outlined"
            color="error"
            onClick={() => onRevoke(device)}
            disabled={busy}
            sx={{ ...dangerOutlinedButtonSx, whiteSpace: "nowrap" }}
          >
            {pending ? "Rechazar" : "Quitar acceso"}
          </Button>
        </MDBox>
      )}
    </MDBox>
  );
}

function DeviceList({ devices, onApprove, onRevoke, busy }) {
  return (
    <MDBox display="flex" flexDirection="column" gap={1}>
      {devices.map((device) => (
        <DeviceRow
          key={device.Id}
          device={device}
          onApprove={onApprove}
          onRevoke={onRevoke}
          busy={busy}
        />
      ))}
    </MDBox>
  );
}

export default function AuthorizedDevicesPanel() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [actionId, setActionId] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });

  const loadDevices = async () => {
    setLoading(true);
    setError("");
    try {
      setDevices(await AuthorizedDeviceService.getAuthorizedDevices());
    } catch (requestError) {
      setError(
        requestError.response?.status === 403
          ? "No tenés permisos para ver dispositivos autorizados."
          : requestError.response?.data?.message ||
              requestError.response?.data?.error ||
              "No se pudo cargar el listado. Recargá la página para intentarlo de nuevo."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDevices();
  }, []);

  const runAction = async (device, action) => {
    setActionId(device.Id);
    try {
      if (action === "approve") {
        await AuthorizedDeviceService.approveDevice(device.Id);
        setSnack({ open: true, message: "Dispositivo aprobado.", severity: "success" });
      } else {
        await AuthorizedDeviceService.revokeDevice(device.Id);
        setSnack({
          open: true,
          message: device.Status === "pending" ? "Solicitud rechazada." : "Acceso quitado.",
          severity: "success",
        });
      }
      await loadDevices();
      return true;
    } catch (requestError) {
      setSnack({
        open: true,
        message:
          requestError.response?.data?.message ||
          requestError.response?.data?.error ||
          "No se pudo actualizar el dispositivo.",
        severity: "error",
      });
      return false;
    } finally {
      setActionId(null);
    }
  };

  const pending = devices.filter((device) => device.Status === "pending");
  const active = devices.filter((device) => device.Status === "active");
  const busy = loading || actionId !== null;

  return (
    <MDBox>
      <SecuritySectionTitle mb={3}>
        {pending.length > 0 ? "Solicitudes de acceso pendientes" : "Solicitudes de acceso"}
      </SecuritySectionTitle>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading && devices.length === 0 ? (
        <MDBox display="flex" alignItems="center" gap={1.5} py={2}>
          <CircularProgress size={22} />
          <MDTypography variant="body2">Cargando dispositivos...</MDTypography>
        </MDBox>
      ) : devices.length === 0 && error ? null : (
        <>
          {pending.length === 0 ? (
            <MDTypography variant="body2" color="text" mb={3}>
              No hay solicitudes de dispositivos pendientes.
            </MDTypography>
          ) : (
            <MDBox component="section" mb={3}>
              <SecurityColumnHeaders
                columns={pendingColumnTitles}
                gridTemplateColumns={authorizedColumns}
              />
              <DeviceList
                devices={pending}
                onApprove={(device) => runAction(device, "approve")}
                onRevoke={setConfirm}
                busy={busy}
              />
            </MDBox>
          )}

          <MDBox component="section">
            <SecuritySectionTitle>Dispositivos autorizados</SecuritySectionTitle>
            {active.length === 0 ? (
              <MDTypography variant="body2" color="text">
                No hay dispositivos autorizados.
              </MDTypography>
            ) : (
              <>
                <SecurityColumnHeaders
                  columns={authorizedColumnTitles}
                  gridTemplateColumns={authorizedColumns}
                />
                <DeviceList devices={active} onRevoke={setConfirm} busy={busy} />
              </>
            )}
          </MDBox>
        </>
      )}

      <AppActionDialog
        open={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        onConfirm={async () => {
          if (!confirm || busy) return;
          const succeeded = await runAction(confirm, "revoke");
          if (succeeded) setConfirm(null);
        }}
        title={
          confirm?.Status === "pending"
            ? "¿Rechazar esta solicitud?"
            : "¿Quitar el acceso a este dispositivo?"
        }
        description={`${confirm?.Email || "Este usuario"} · ${
          confirm?.Label || getDeviceSummary(confirm?.UserAgent)
        }`}
        confirmLabel={
          actionId !== null
            ? "Procesando..."
            : confirm?.Status === "pending"
              ? "Rechazar"
              : "Quitar acceso"
        }
        confirmColor="error"
        busy={busy}
      />

      <AppSnackbar
        open={snack.open}
        onClose={() => setSnack((previous) => ({ ...previous, open: false }))}
        severity={snack.severity}
        message={snack.message}
      />
    </MDBox>
  );
}
